import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import api from '@/lib/api';
import { getApiErrorMessage, usePartnerAuthStore } from '@/stores/partnerAuthStore';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { PromoCodeFormDialog } from '@/components/promo-codes/PromoCodeFormDialog';
import { PROMO_STATUS_LABELS, describeDiscount, describeUse, formatExpiry, type PromoCode } from '@/types/promoCodes';

export function PromoCodesPage() {
  const { partner } = usePartnerAuthStore();
  const isAdmin = partner?.role === 'admin';

  const [codes, setCodes] = useState<PromoCode[]>([]);
  // undefined: dialog closed. null: creating. A code: editing it.
  const [dialogCode, setDialogCode] = useState<PromoCode | null | undefined>(undefined);
  const [reloadCount, setReloadCount] = useState(0);
  const [loadedCount, setLoadedCount] = useState<number | null>(null);
  const isLoading = loadedCount !== reloadCount;

  useEffect(() => {
    // `active` makes a superseded response harmless, as on the Plans page.
    let active = true;

    void (async () => {
      try {
        const response = await api.get('/promo-codes');
        if (!active) return;
        setCodes(response.data.data);
      } catch (error) {
        if (!active) return;
        // An empty table would read as "no codes exist", which is a worse lie
        // than an error.
        setCodes([]);
        toast.error(getApiErrorMessage(error), { id: 'promo-codes-error' });
      }
      if (active) setLoadedCount(reloadCount);
    })();

    return () => {
      active = false;
    };
  }, [reloadCount]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold">Promo codes</h1>
          <p className="text-muted-foreground">
            Every code, including inactive ones and how many groups have used them.
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => setDialogCode(null)}>
            <Plus className="mr-2 h-4 w-4" />
            Add promo code
          </Button>
        )}
      </div>

      {codes.length === 0 ? (
        <p className="py-10 text-center text-sm text-muted-foreground">
          {isLoading ? 'Loading promo codes.' : 'No promo codes exist yet.'}
        </p>
      ) : (
        <Card>
          <CardContent className="overflow-x-auto p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Gives</TableHead>
                  <TableHead>Used</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead>Status</TableHead>
                  {isAdmin && <TableHead />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {codes.map((code) => (
                  <TableRow key={code.id} className={code.status === 'active' ? undefined : 'text-muted-foreground'}>
                    <TableCell className="font-mono font-medium">{code.code}</TableCell>
                    <TableCell>{describeDiscount(code)}</TableCell>
                    <TableCell>{describeUse(code)}</TableCell>
                    <TableCell>{formatExpiry(code.expiresOn)}</TableCell>
                    <TableCell>
                      <Badge variant={code.status === 'active' ? 'default' : 'secondary'}>
                        {PROMO_STATUS_LABELS[code.status]}
                      </Badge>
                    </TableCell>
                    {isAdmin && (
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => setDialogCode(code)}>Edit</Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {dialogCode !== undefined && (
        <PromoCodeFormDialog
          open
          onOpenChange={(open) => !open && setDialogCode(undefined)}
          code={dialogCode}
          onSaved={() => setReloadCount((count) => count + 1)}
        />
      )}
    </div>
  );
}
